import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { endpoints, API_BASE_URL ,CHROME_STORE_URL} from '../services/api';
import '../styles/pages/LandingPage.css';
import { PRICES, EXTRA_SEAT_YEARLY, TRIAL_DAYS } from '../config/pricing';
import { FREE_MODE } from '../config/freeMode';
import { getFriendlyError } from '../components/errorUtils';
import ReactGA from 'react-ga4';
import LandingTour from '../components/LandingTour/LandingTour';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

async function detectGateway() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'Asia/Calcutta' || tz === 'Asia/Kolkata') return { gateway: 'razorpay', razorpay_key_id: null };
  } catch (_) {}
  try {
    const res = await fetch(`${API_BASE_URL}/api/billing/detect-provider`, {
      method: 'POST',
      signal: AbortSignal.timeout(4000),
    });
    if (res.ok) return await res.json();
  } catch (_) {}
  return { gateway: 'stripe', razorpay_key_id: null };
}

function trackChromeStoreClick(source) {
  ReactGA.event('chrome_store_click', {
    click_source: source,
  });
}

function LandingPage() {
  const [isYearly, setIsYearly] = useState(true);
  const [loading,   setLoading]   = useState(null);
  const [error,     setError]     = useState('');
  const [gateway,       setGateway]       = useState('stripe');
  const [isIndia,       setIsIndia]       = useState(false);
  const [geoReady,      setGeoReady]      = useState(false);
  const [razorpayKeyId, setRazorpayKeyId] = useState(null);

  const { token, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    detectGateway().then(({ gateway: gw, razorpay_key_id }) => {
      setGateway(gw);
      setIsIndia(gw === 'razorpay');
      setRazorpayKeyId(razorpay_key_id);
      setGeoReady(true);
    });
  }, []);

  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id === 'pricing' || id === 'how-it-works') {
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  const priceTable = isIndia ? PRICES.inr : PRICES.usd;
  const price      = (plan) => isYearly ? priceTable[plan].yearly  : priceTable[plan].monthly;
  const period     = (plan) => isYearly ? priceTable[plan].period_yr : priceTable[plan].period_mo;
  const saveLabel  = (plan) => priceTable[plan].saveLabel;

  const handleUpgradePlan = useCallback(async (plan) => {
    if (!token) { navigate('/login'); return; }
    setLoading(plan);
    setError('');
    try {
      const res = await fetch(endpoints.payments.checkoutSession, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, gateway, billing_period: isYearly ? 'yearly' : 'monthly' }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to start checkout');
      }
      const data = await res.json();
      if (gateway === 'stripe') { window.location.href = data.url; return; }
      if (gateway === 'razorpay') {
        const loaded = await loadRazorpayScript();
        if (!loaded) throw new Error('Razorpay failed to load. Please try again.');
        const options = {
          key: razorpayKeyId || data.key_id, subscription_id: data.subscription_id,
          name: 'Michii', description: `${plan === 'individual' ? 'Individual' : 'Teams'} plan`,
          image: '/michii_logo.png',
          handler: (response) => navigate(`/payment/success?gateway=razorpay&plan=${plan}&razorpay_payment_id=${response.razorpay_payment_id}`),
          prefill: { email: user?.email || '' },
          theme: { color: '#E8521C' },
          modal: { ondismiss: () => setLoading(null) },
        };
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (resp) => { setError(getFriendlyError(`Payment failed: ${resp.error.description}`,'Landing Page')); setLoading(null); });
        rzp.open();
        return;
      }
    } catch (err) {
      setError(getFriendlyError(err.message, 'Landing Page'));
    } finally {
      if (gateway !== 'razorpay') setLoading(null);
    }
  }, [token, gateway, user, navigate, isYearly, razorpayKeyId]);

  const handleUpgrade      = () => handleUpgradePlan('individual');
  const handleUpgradeTeams = () => handleUpgradePlan('teams');
  const isCurrentPlan = (plan) => user?.plan === plan;
  const isLoadingPlan = (plan) => loading === plan;


  const tourSteps = [
    { selector: '#lp-cta',   say: 'Click “Add to Chrome”',   panel: 'Install the extension. It takes about 10 seconds.' },
    { selector: '#lp-after', say: 'Same SOP, as a guide',    panel: 'This is what your 14-page doc turns into.' },
    { selector: '#lp-rail',  say: 'This is the whole setup', panel: 'Record, let Michii write it, share the link.' },
    FREE_MODE
      ? { selector: '#lp-col-free', say: 'Everything is free right now', panel: 'Full Individual features, free during the beta.' }
      : { selector: '#lp-col-ind',  say: 'Most solo folks pick this',    panel: `Individual: unlimited guides for ${price('individual')}${period('individual')}.` },
  ];

  const planButton = (plan, label, cls) => {
    if (isCurrentPlan(plan)) {
      return <button className={`lp-btn sm ${cls}`} disabled>✓ Current plan</button>;
    }
    return (
      <button
        className={`lp-btn sm ${cls}`}
        onClick={plan === 'individual' ? handleUpgrade : handleUpgradeTeams}
        disabled={!!loading || !geoReady}
      >
        {isLoadingPlan(plan) ? 'Redirecting…' : token ? label : 'Get started'}
      </button>
    );
  };

  return (
    <div className="landing-page">

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-wrap lp-hero__grid">
          <div>
            <h1 className="lp-hero__h1">Do it once.</h1>
            <p className="lp-hero__h1b">Michii shows <em>everyone else</em> where to click.</p>
            <p className="lp-hero__lede">
              Turn on the extension, click through a task on any website, and Michii turns every
              click into a step. Your team runs it as a live walkthrough in their own browser, or
              opens it as a PDF.
            </p>
            <div className="lp-hero__actions">
              <a
                id="lp-cta"
                href={CHROME_STORE_URL}
                className="lp-btn"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackChromeStoreClick('hero_cta')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" /><circle cx="12" cy="12" r="4" fill="currentColor" /></svg>
                Add to Chrome, free
              </a>
              <a href="#how-it-works" className="lp-tlink">See how it works</a>
            </div>
            <div className="lp-hero__meta">
              <div><b>Chrome + Edge</b>Browsers</div>
              <div><b>0 lines</b>Of code to set up</div>
              <div><b>PII blur</b>Built in</div>
            </div>
          </div>

          <div>
            <div className="lp-browser" role="img" aria-label="Example: a Michii guide playing on an online store's order page, with the Refund button highlighted">
              <div className="lp-browser__bar">
                <div className="lp-browser__dots"><i /><i /><i /></div>
                <div className="lp-browser__url">admin.larkspurgoods.com/orders/4417</div>
              </div>
              <div className="lp-app">
                <aside><span>Home</span><span className="on is-ringed">Orders</span><span>Products</span><span>Customers</span><span>Analytics</span></aside>
                <div className="lp-app__main">
                  <div className="lp-app__row"><h4>Order #4417</h4><span className="lp-app__pill">Fulfilled</span></div>
                  <div className="lp-app__order">
                    <div><span>Linen apron, rust × 2</span><span className="lp-mono">$64.00</span></div>
                    <div><span>Shipping</span><span className="lp-mono">$7.40</span></div>
                    <div><span>Total</span><span className="lp-mono">$71.40</span></div>
                  </div>
                  <div className="lp-app__row">
                    <span className="lp-app__cust">Priya Raman · priya.r@…</span>
                    <div className="lp-app__btns"><span>Print</span><span className="is-ringed">Refund</span><span className="dark">More</span></div>
                  </div>
                </div>
              </div>
              <span className="lp-marker lp-marker--1" aria-hidden="true">① →</span>
              <span className="lp-marker lp-marker--2" aria-hidden="true">② →</span>
            </div>
            <p className="lp-caption"><span className="lp-hand">what your team sees</span><span>· “Refund an order” · 6 steps · 1m 40s</span></p>
          </div>
        </div>
      </section>

      {/* ── BEFORE / AFTER ───────────────────────────────────────────────────── */}
      <section className="lp-block">
        <div className="lp-wrap">
          <div className="lp-shead">
            <h2>Same process. One of these gets followed.</h2>
            <p>The left one is the doc your team wrote last spring. The right one is the same process recorded in Michii. It runs on the live page and circles each button as you reach it.</p>
          </div>
          <div className="lp-ba">
            <div>
              <div className="lp-ba__label"><span className="lp-eyebrow">Before</span></div>
              <div className="lp-doc" role="img" aria-label="An old, outdated SOP document">
                <div className="lp-doc__fn">SOP_refunds_v7_FINAL(2).docx · 14 pages</div>
                <div className="l" style={{ width: '62%' }} /><div className="l" /><div className="l" style={{ width: '88%' }} />
                <div className="shot" />
                <div className="l" /><div className="l" style={{ width: '74%' }} /><div className="l" style={{ width: '91%' }} />
                <div className="shot" />
                <div className="l" style={{ width: '55%' }} /><div className="l" />
                <div className="lp-doc__stamp">Last edited<br />11 months ago</div>
              </div>
            </div>
            <div>
              <div className="lp-ba__label"><span className="lp-eyebrow">After</span><span className="lp-hand">same thing</span></div>
              <div className="lp-guide" id="lp-after">
                <header>
                  <div>
                    <h3>Refund an order in the store admin</h3>
                    <p className="lp-guide__by">Recorded by Dev M. · Updated 2 days ago</p>
                  </div>
                  <span className="lp-badge lp-badge--pub">Public</span>
                </header>
                <ol>
                  <li><span className="n">01</span><span>Open <b>Orders</b> in the sidebar</span><span className="t">0:08</span></li>
                  <li><span className="n">02</span><span>Search the order number</span><span className="t">0:15</span></li>
                  <li className="cur"><span className="n">03</span><span>Click <b>Refund</b></span><span className="t">0:21</span></li>
                  <li><span className="n">04</span><span>Pick items and restock option</span><span className="t">0:34</span></li>
                  <li><span className="n">05</span><span>Add a reason for the customer</span><span className="t">0:52</span></li>
                  <li><span className="n">06</span><span>Confirm refund of <span className="lp-mono">$71.40</span></span><span className="t">1:40</span></li>
                </ol>
              </div>
            </div>
          </div>

          <div className="lp-stop">
            <div><s>Record a 9-minute screen video</s></div>
            <div><s>Paste 40 screenshots into a doc</s></div>
            <div><s>Answer “where’s that button?” on Slack again</s></div>
            <div><s>Redo the doc every time the UI changes</s><span>Michii flags steps that break</span></div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="lp-block">
        <div className="lp-wrap">
          <div className="lp-shead">
            <h2>Three steps to your first guide.</h2>
            <p>The dots below are the same ones people see in the playback panel, so what you read here is what your team will see.</p>
          </div>
          <div className="lp-rail" id="lp-rail">
            <div className="s done"><p className="k">Step 1 / 3</p><h3>Record</h3><p>Click the Michii icon and do the task the way you normally would, on any site.</p></div>
            <div className="s"><p className="k">Step 2 / 3</p><h3>Michii writes the steps</h3><p>Each click becomes a step with a description, a screenshot, and a ring around the right element. Sensitive fields are blurred.</p></div>
            <div className="s"><p className="k">Step 3 / 3</p><h3>Share a link</h3><p>Teammates press play and Michii walks them through it on the real page. Or export a clean PDF.</p></div>
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────────── */}
      <section id="pricing" className="lp-block">
        <div className="lp-wrap">
          {FREE_MODE ? (
            <>
              <div className="lp-shead">
                <h2>Pricing</h2>
                <p>Free access during the beta. Unlimited guides, AI assistance and PDF exports, without entering a credit card.</p>
              </div>
              <div className="lp-tablewrap">
                <table className="lp-table lp-table--2">
                  <thead>
                    <tr>
                      <th scope="col"><span className="lp-eyebrow">Compare plans</span></th>
                      <th scope="col" className="col-hi" id="lp-col-free">
                        <div className="plan">Free beta</div>
                        <div className="price">$0</div>
                        <div className="sub">All Individual features, free today</div>
                      </th>
                      <th scope="col">
                        <div className="plan">Enterprise</div>
                        <div className="price">Custom</div>
                        <div className="sub">For teams that need native playback and SSO</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><th scope="row">Guides</th><td className="y col-hi">Unlimited</td><td className="y">Unlimited</td></tr>
                    <tr><th scope="row">AI assistant</th><td className="y col-hi">Yes</td><td className="y">Yes</td></tr>
                    <tr><th scope="row">PDF export</th><td className="y col-hi">No watermark</td><td className="y">No watermark</td></tr>
                    <tr><th scope="row">Blur sensitive areas in images</th><td className="y col-hi">Yes</td><td className="y">Yes</td></tr>
                    <tr><th scope="row">Private and public sharing</th><td className="y col-hi">Yes</td><td className="y">Plus shared library and roles</td></tr>
                    <tr><th scope="row">SDK for in-product guide playback</th><td className="n col-hi">No</td><td className="y">Yes</td></tr>
                    <tr><th scope="row">Guide health monitoring and break alerts</th><td className="n col-hi">No</td><td className="y">Yes</td></tr>
                    <tr><th scope="row">SSO and SAML login</th><td className="n col-hi">No</td><td className="y">Yes</td></tr>
                    <tr><th scope="row">Priority support with SLA</th><td className="n col-hi">No</td><td className="y">Yes</td></tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td />
                      <td className="col-hi">
                        <a
                          href={CHROME_STORE_URL}
                          className="lp-btn sm"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackChromeStoreClick('free_mode_card')}
                        >
                          Get started free
                        </a>
                      </td>
                      <td><a href="mailto:michii.support@gmail.com" className="lp-btn sm quiet">Contact sales</a></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="lp-shead">
                <h2>Pricing</h2>
                <p>Every plan starts with a {TRIAL_DAYS}-day free trial, no credit card. People who only watch your guides never pay.</p>
              </div>

              {error && (
                <div className="lp-error" role="alert">
                  {error}
                  <button onClick={() => setError('')} aria-label="Dismiss error">✕</button>
                </div>
              )}

              <div className="lp-tog" role="group" aria-label="Billing period">
                <button type="button" aria-pressed={!isYearly} onClick={() => setIsYearly(false)}>Monthly</button>
                <button type="button" aria-pressed={isYearly} onClick={() => setIsYearly(true)}>Yearly</button>
              </div>

              <div className="lp-tablewrap">
                <table className="lp-table">
                  <thead>
                    <tr>
                      <th scope="col"><span className="lp-eyebrow">Compare plans</span></th>
                      <th scope="col">
                        <div className="plan">Free trial</div>
                        <div className="price">{isIndia ? '₹0' : '$0'} <small>/{TRIAL_DAYS} days</small></div>
                        <div className="sub">Everything in Individual. Your guides keep playing after it ends.</div>
                      </th>
                      <th scope="col" className="col-hi" id="lp-col-ind">
                        <div className="plan">Individual</div>
                        <div className="price">{price('individual')} <small>{period('individual')}</small></div>
                        <div className="sub">{isYearly ? saveLabel('individual') : 'For one person writing SOPs'}</div>
                      </th>
                      <th scope="col">
                        <div className="plan">Teams</div>
                        <div className="price">{price('teams')} <small>{period('teams')}</small></div>
                        <div className="sub">
                          {saveLabel('teams')}
                          {isYearly && <> · +{isIndia ? EXTRA_SEAT_YEARLY.inr : EXTRA_SEAT_YEARLY.usd}/seat after 5</>}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><th scope="row">Guides</th><td className="y">Unlimited</td><td className="y col-hi">Unlimited</td><td className="y">Unlimited</td></tr>
                    <tr><th scope="row">Share links and live walkthroughs</th><td className="y">Yes</td><td className="y col-hi">Yes</td><td className="y">Yes</td></tr>
                    <tr><th scope="row">PDF export</th><td className="y">No watermark</td><td className="y col-hi">No watermark</td><td className="y">No watermark</td></tr>
                    <tr><th scope="row">AI assistant</th><td className="y">Yes</td><td className="y col-hi">300 questions/mo</td><td className="y">300 questions/mo per seat</td></tr>
                    <tr><th scope="row">Step analytics</th><td className="y">Viewer tracking</td><td className="y col-hi">Viewer tracking</td><td className="y">Workspace analytics and rank</td></tr>
                    <tr><th scope="row">Shared workspace and health checks</th><td className="n">No</td><td className="n col-hi">No</td><td className="y">Yes</td></tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td />
                      <td>
                        <a
                          href={CHROME_STORE_URL}
                          className="lp-btn sm quiet"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackChromeStoreClick('free_plan_card')}
                        >
                          Start free trial
                        </a>
                      </td>
                      <td className="col-hi">{planButton('individual', 'Upgrade now', '')}</td>
                      <td>{planButton('teams', 'Upgrade to Teams', 'quiet')}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {geoReady && (
                <p className="lp-fine">
                  {isIndia
                    ? 'Prices in INR. Pay with Razorpay (UPI, net banking, cards).'
                    : 'Prices in USD. Pay with Stripe (cards, Apple Pay, Google Pay).'}
                </p>
              )}
              <p className="lp-fine">Enterprise needs SSO, SAML or in-product playback through the SDK. Email michii.support@gmail.com.</p>
            </>
          )}
        </div>
      </section>

      <LandingTour steps={tourSteps} />
    </div>
  );
}

export default LandingPage;
